/**
 * Auction PLP/PDP: fetch JSON from app proxy and render suggestions in the header.
 * Requires body[data-storefront-search-scope="auction"].
 * Layout: Did you mean + Product searches only (no Related Products / Categories).
 * Product cap: 10 desktop, 5 mobile. Single product column when ≤5 results.
 */
(function () {
  'use strict';

  var MIN_QUERY_LEN = 3;
  var DEBOUNCE_MS = 150;
  var MAX_SUGGESTION_PRODUCTS_DESKTOP = 10;
  var MAX_SUGGESTION_PRODUCTS_MOBILE = 5;
  var OVERLAY_CLASS = 'ra-search-overlay';
  var BODY_OPEN_CLASS = 'ra-search-suggestions-open';

  /**
   * @returns {number}
   */
  function getSuggestionProductLimit() {
    return window.innerWidth <= 767
      ? MAX_SUGGESTION_PRODUCTS_MOBILE
      : MAX_SUGGESTION_PRODUCTS_DESKTOP;
  }

  /**
   * @returns {string}
   */
  function getAuctionSearchUrl() {
    var body = document.body;
    if (!body || body.dataset.storefrontSearchScope !== 'auction') return '';
    var u = body.dataset.auctionSearchUrl;
    return typeof u === 'string' ? u.trim() : '';
  }

  /** Hard fallback when Liquid/metaobject listing URL is missing. */
  var AUCTION_LISTING_FALLBACK_PATH = '/collections/online-auctions';

  /**
   * Auction PLP/PDP listing URL for search navigation.
   * Never return empty on auction scope — empty listing used to allow native form
   * submit to /search, where Prefixbox merchandising redirects (e.g. "ring" → rings).
   *
   * @returns {string}
   */
  function getAuctionListingUrl() {
    var body = document.body;
    if (!body || body.dataset.storefrontSearchScope !== 'auction') return '';
    var u = body.dataset.auctionListingUrl;
    if (typeof u === 'string' && u.trim()) return u.trim();
    return new URL(AUCTION_LISTING_FALLBACK_PATH, window.location.origin).pathname;
  }

  /**
   * Reads Prefixbox config from body data attributes (set in theme.liquid from
   * theme settings). Returns null when Prefixbox is disabled or misconfigured,
   * in which case the default app-proxy auction search is used.
   *
   * @returns {{ tracker: string, apiKey: string } | null}
   */
  function getPrefixboxConfig() {
    var body = document.body;
    if (!body || body.dataset.prefixboxEnabled !== 'true') return null;
    var tracker = (body.dataset.prefixboxAutocompleteTracker || '').trim();
    var apiKey = (body.dataset.prefixboxApiKey || '').trim();
    if (!tracker || !apiKey) return null;
    return { tracker: tracker, apiKey: apiKey };
  }

  /**
   * @returns {string}
   */
  function getCurrentSearchQuery() {
    return new URLSearchParams(window.location.search).get('q') || '';
  }

  /**
   * @param {string} baseUrl
   * @param {string} q
   * @returns {string}
   */
  function buildAuctionListingSearchUrl(baseUrl, q) {
    var url = new URL(baseUrl || window.location.pathname, window.location.origin);
    var cleaned = (q || '').trim();
    if (cleaned) {
      url.searchParams.set('q', cleaned);
    } else {
      url.searchParams.delete('q');
    }
    return url.toString();
  }

  /**
   * Normalize path for compare (trailing slash, case).
   * @param {string} pathname
   * @returns {string}
   */
  function normalizePathname(pathname) {
    var p = String(pathname || '/');
    if (p.length > 1 && p.charAt(p.length - 1) === '/') p = p.slice(0, -1);
    return p.toLowerCase();
  }

  /**
   * True when navigating would land on the same document (pathname + q).
   * Avoids a full reload that feels like a second redirect when already searching.
   *
   * @param {string} targetHref
   * @returns {boolean}
   */
  function isSameAuctionSearchLocation(targetHref) {
    try {
      var target = new URL(targetHref, window.location.origin);
      var here = window.location;
      if (normalizePathname(target.pathname) !== normalizePathname(here.pathname)) {
        return false;
      }
      var tq = (target.searchParams.get('q') || '').trim().toLowerCase();
      var hq = (new URLSearchParams(here.search).get('q') || '').trim().toLowerCase();
      return tq === hq;
    } catch (e) {
      return false;
    }
  }

  /**
   * Single navigation hop to auction listing ?q= (or no-op if already there).
   *
   * @param {string} listingUrl
   * @param {string} q
   */
  function navigateToAuctionListingSearch(listingUrl, q) {
    var href = buildAuctionListingSearchUrl(
      listingUrl || getAuctionListingUrl() || AUCTION_LISTING_FALLBACK_PATH,
      q
    );
    if (isSameAuctionSearchLocation(href)) return;
    window.location.assign(href);
  }

  /**
   * @returns {boolean}
   */
  function isAuctionStorefrontScope() {
    return !!(document.body && document.body.dataset.storefrontSearchScope === 'auction');
  }

  /**
   * Keep header search form pointed at the auction listing (never /search).
   * Prefixbox Integration merchandising can rewrite action or intercept Enter
   * and send shoppers to retail collections (e.g. "ring" → /collections/ring).
   *
   * @param {HTMLFormElement | null} form
   */
  function lockAuctionSearchForm(form) {
    if (!form) return;
    var listing = getAuctionListingUrl() || AUCTION_LISTING_FALLBACK_PATH;
    try {
      form.setAttribute('action', listing);
      form.action = listing;
    } catch (e) {
      /* ignore */
    }
    var input =
      form.querySelector('[data-ra-search-input]') ||
      form.querySelector('[data-search-input]');
    if (input && input.name === 'q') {
      input.name = 'ra_q';
      input.setAttribute('data-ra-search-input', '');
    }
  }

  /**
   * Hard-block Prefixbox Integration post-load merchandising redirects.
   * Prefixbox uses window.location.href=… which bypasses Location.prototype
   * patches — Navigation API navigate.preventDefault is the reliable path.
   * Primary install is theme.liquid <head>; this refreshes the listing
   * allowlist and installs Navigation API / Location backups if missing.
   */
  function installAuctionMerchandisingNavGuard() {
    if (!isAuctionStorefrontScope()) return;

    var listing = getAuctionListingUrl() || AUCTION_LISTING_FALLBACK_PATH;
    if (listing) window.__raAuctionListingUrl = listing;
    window.__raAuctionStorefrontScope = true;

    var FALLBACK_HANDLES = {
      'online-auctions': true,
      'jewellery-auctions': true
    };

    function collectionHandleFromPath(pathname) {
      var m = String(pathname || '').match(/^\/collections\/([^\/?#]+)/i);
      if (!m) return '';
      try {
        return decodeURIComponent(m[1]).toLowerCase();
      } catch (e) {
        return String(m[1]).toLowerCase();
      }
    }

    function allowedCollectionHandles() {
      var handles = {};
      Object.keys(FALLBACK_HANDLES).forEach(function (key) {
        handles[key] = true;
      });
      var listingUrl =
        (typeof window.__raAuctionListingUrl === 'string' &&
          window.__raAuctionListingUrl) ||
        getAuctionListingUrl() ||
        '';
      if (listingUrl) {
        try {
          var path = new URL(listingUrl, window.location.origin).pathname;
          var h = collectionHandleFromPath(path);
          if (h) handles[h] = true;
        } catch (e) {
          /* ignore */
        }
      }
      return handles;
    }

    function isAuctionCollectionHandle(handle) {
      return String(handle || '').indexOf('-auction') !== -1;
    }

    /* Same shopper-tap allowance as layout/theme.liquid (shared window state). */
    var SHOPPER_TAP_TTL_MS = 3000;

    function samePathKey(url) {
      try {
        var u = new URL(url, window.location.origin);
        if (u.origin !== window.location.origin) return '';
        return u.pathname.replace(/\/$/, '') || '/';
      } catch (e) {
        return '';
      }
    }

    function rememberShopperTap(e) {
      if (!e.isTrusted) return;
      var el = e.target;
      var a = el && el.closest ? el.closest('a[href]') : null;
      if (!a || !a.href) return;
      window.__raRecentShopperTap = {
        path: samePathKey(a.href),
        at: Date.now()
      };
    }

    if (!window.__raShopperTapTrackerInstalled) {
      window.__raShopperTapTrackerInstalled = true;
      document.addEventListener('pointerdown', rememberShopperTap, true);
      document.addEventListener('click', rememberShopperTap, true);
    }

    function isRecentShopperTap(url) {
      var tap = window.__raRecentShopperTap;
      if (!tap || !tap.path) return false;
      if (Date.now() - tap.at > SHOPPER_TAP_TTL_MS) return false;
      return samePathKey(url) === tap.path;
    }

    function shouldBlock(url) {
      if (
        !isAuctionStorefrontScope() &&
        window.__raAuctionStorefrontScope !== true
      ) {
        return false;
      }
      if (isRecentShopperTap(url)) return false;
      try {
        var u = new URL(url, window.location.origin);
        if (u.origin !== window.location.origin) return false;
        var path = u.pathname.replace(/\/$/, '') || '/';
        if (path === '/search' || path.indexOf('/search/') === 0) return true;
        var handle = collectionHandleFromPath(path);
        if (!handle) return false;
        if (isAuctionCollectionHandle(handle)) return false;
        return !allowedCollectionHandles()[handle];
      } catch (e) {
        return false;
      }
    }

    window.__raShouldBlockAuctionMerchNav = shouldBlock;

    function warnBlocked(url) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[RA] Blocked Prefixbox merchandising navigation:', url);
      }
    }

    function runOrBlock(url, proceed) {
      if (shouldBlock(url)) {
        warnBlocked(url);
        return undefined;
      }
      return proceed();
    }

    try {
      var nav = window.navigation;
      if (
        nav &&
        typeof nav.addEventListener === 'function' &&
        !window.__raAuctionNavApiGuardInstalled
      ) {
        window.__raAuctionNavApiGuardInstalled = true;
        nav.addEventListener('navigate', function (event) {
          try {
            if (event.userInitiated) return;
            var dest =
              event.destination && event.destination.url
                ? event.destination.url
                : '';
            if (!dest || !shouldBlock(dest)) return;
            event.preventDefault();
            warnBlocked(dest);
          } catch (e) {
            /* ignore */
          }
        });
      }
    } catch (navErr) {
      /* Navigation API unavailable */
    }

    if (window.__raAuctionMerchNavGuardInstalled) return;
    window.__raAuctionMerchNavGuardInstalled = true;

    try {
      var proto = Location.prototype;
      var origAssign = proto.assign;
      var origReplace = proto.replace;
      if (typeof origAssign === 'function') {
        proto.assign = function (url) {
          var self = this;
          var args = arguments;
          return runOrBlock(url, function () {
            return origAssign.apply(self, args);
          });
        };
      }
      if (typeof origReplace === 'function') {
        proto.replace = function (url) {
          var self = this;
          var args = arguments;
          return runOrBlock(url, function () {
            return origReplace.apply(self, args);
          });
        };
      }
      var hrefDesc = Object.getOwnPropertyDescriptor(proto, 'href');
      if (hrefDesc && hrefDesc.set && hrefDesc.get) {
        Object.defineProperty(proto, 'href', {
          configurable: true,
          enumerable: hrefDesc.enumerable,
          get: function () {
            return hrefDesc.get.call(this);
          },
          set: function (url) {
            var self = this;
            return runOrBlock(url, function () {
              return hrefDesc.set.call(self, url);
            });
          }
        });
      }
    } catch (err) {
      /* Location.prototype may be non-configurable */
    }
  }

  /**
   * Document-level capture guards must win over Prefixbox Integration, which
   * often registers on document/window before deferred ra-search.js bindInput.
   * Also install when autocomplete API URL is missing so Enter still works.
   */
  function installAuctionSearchSubmitGuard() {
    if (window.__raSearchSubmitGuardInstalled) return;
    window.__raSearchSubmitGuardInstalled = true;

    function isRaSearchForm(form) {
      return !!(
        form &&
        form instanceof HTMLFormElement &&
        form.classList &&
        form.classList.contains('global-header__search')
      );
    }

    function raInputFromForm(form) {
      return (
        form.querySelector('[data-ra-search-input]') ||
        form.querySelector('[data-search-input]')
      );
    }

    function handleRaSearchNavigate(form, input) {
      lockAuctionSearchForm(form);
      navigateToAuctionListingSearch(
        getAuctionListingUrl() || AUCTION_LISTING_FALLBACK_PATH,
        input ? input.value : ''
      );
    }

    document.addEventListener(
      'submit',
      function (e) {
        if (!isAuctionStorefrontScope()) return;
        var form = e.target;
        if (!isRaSearchForm(form)) return;
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }
        handleRaSearchNavigate(form, raInputFromForm(form));
      },
      true
    );

    function onEnterKey(e) {
      if (e.key !== 'Enter' && e.keyCode !== 13) return;
      if (!isAuctionStorefrontScope()) return;
      var t = e.target;
      if (!(t instanceof HTMLElement)) return;
      if (
        !t.matches(
          '.global-header__search input[data-search-input], .global-header__search input[data-ra-search-input]'
        )
      ) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation();
      }
      var form = t.closest('form.global-header__search');
      handleRaSearchNavigate(form, t);
    }

    // keydown + keypress: Prefixbox Integration has used both historically.
    document.addEventListener('keydown', onEnterKey, true);
    document.addEventListener('keypress', onEnterKey, true);
  }

  /**
   * Decode UTF-8 bytes that were misread as Latin-1/Windows-1252 (e.g. â€" → —).
   *
   * @param {string} str
   * @returns {string}
   */
  function decodeMojibake(str) {
    if (!str || typeof str !== 'string') return str || '';
    if (!/[Ãâ]/.test(str)) return str;

    try {
      var bytes = new Uint8Array(str.length);
      for (var i = 0; i < str.length; i++) {
        bytes[i] = str.charCodeAt(i) & 0xff;
      }
      var decoded = new TextDecoder('utf-8').decode(bytes);
      if (decoded && decoded.indexOf('\uFFFD') === -1) {
        return decoded;
      }
    } catch (e) {
      /* fall through */
    }

    return str
      .replace(/â€"/g, '\u2014')
      .replace(/â€™/g, '\u2019')
      .replace(/â€œ/g, '\u201C')
      .replace(/â€/g, '\u201D')
      .replace(/â€¦/g, '\u2026');
  }

  /**
   * Escape text for safe HTML insertion.
   *
   * @param {string} str
   * @returns {string}
   */
  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Prefixbox displayText may include <b> highlight markup. Escape all HTML,
   * then re-enable only <b>/<strong> so highlights render safely.
   *
   * @param {string} str
   * @returns {string}
   */
  function sanitizeHighlightHtml(str) {
    var raw = decodeMojibake(str || '');
    if (!raw) return '';
    return raw
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/&lt;(\/?)(b|strong)&gt;/gi, '<$1$2>');
  }

  /**
   * Main Prefixbox bolds the typed query ("fruit juicer" → "<b>fruit</b> juicer").
   * RA autocomplete often bolds the completion ("fruit<b> juicer</b>"); rewrite to
   * match the main-site pattern when we know the query.
   *
   * @param {string} plain
   * @param {string} query
   * @returns {string}
   */
  function highlightQueryMatch(plain, query) {
    var text = stripHtmlTags(plain || '');
    var q = String(query || '').trim();
    if (!text) return '';
    if (!q) return escapeHtml(text);
    var lowerText = text.toLowerCase();
    var lowerQ = q.toLowerCase();
    var idx = lowerText.indexOf(lowerQ);
    if (idx < 0) return escapeHtml(text);
    // Expand to the full word containing the match so "diamon" in "diamond"
    // becomes **diamond**, not **diamon** + "d" (mid-word split).
    var start = idx;
    var end = idx + q.length;
    while (start > 0 && /[A-Za-z0-9]/.test(text.charAt(start - 1))) start -= 1;
    while (end < text.length && /[A-Za-z0-9]/.test(text.charAt(end))) end += 1;
    return (
      escapeHtml(text.slice(0, start)) +
      '<b>' +
      escapeHtml(text.slice(start, end)) +
      '</b>' +
      escapeHtml(text.slice(end))
    );
  }

  /**
   * @param {HTMLElement} el
   * @param {string} highlighted
   * @param {string} [plainFallback]
   * @param {string} [query]
   */
  function setHighlightedText(el, highlighted, plainFallback, query) {
    var plain = decodeMojibake(plainFallback || stripHtmlTags(highlighted || '') || '');
    if (query && plain) {
      el.innerHTML = highlightQueryMatch(plain, query);
      return;
    }
    var html = sanitizeHighlightHtml(highlighted || '');
    if (html && /<(b|strong)>/i.test(html)) {
      el.innerHTML = html;
      return;
    }
    el.textContent = plain || decodeMojibake(highlighted || '');
  }

  /**
   * @param {string} str
   * @returns {string}
   */
  function stripHtmlTags(str) {
    return decodeMojibake(str || '').replace(/<[^>]*>/g, '');
  }

  /**
   * @param {string} title
   * @param {Array<object>} items
   * @param {function(object): HTMLLIElement} renderRow
   * @param {string} [extraClass]
   * @returns {HTMLDivElement | null}
   */
  function buildListSection(title, items, renderRow, extraClass) {
    if (!items || !items.length) return null;
    var wrap = document.createElement('div');
    wrap.className = 'ra-search__section' + (extraClass ? ' ' + extraClass : '');
    var h = document.createElement('div');
    h.className = 'ra-search__section-title';
    h.textContent = title;
    wrap.appendChild(h);
    var ul = document.createElement('ul');
    ul.className = 'ra-search__list';
    for (var i = 0; i < items.length; i++) {
      ul.appendChild(renderRow(items[i]));
    }
    wrap.appendChild(ul);
    return wrap;
  }

  /**
   * @param {{
   *   title?: string,
   *   titleHtml?: string,
   *   url?: string,
   *   image?: string,
   *   query?: string
   * }} p
   */
  function renderProductRow(p) {
    var li = document.createElement('li');
    li.className = 'ra-search__item ra-search__item--product';
    var a = document.createElement('a');
    a.className = 'ra-search__link ra-search__link--product';
    a.href = p.url || '#';
    var plainTitle = stripHtmlTags(p.title || p.titleHtml || '');

    if (p.image) {
      var img = document.createElement('img');
      img.className = 'ra-search__thumb';
      img.src = p.image;
      img.alt = plainTitle;
      img.loading = 'lazy';
      a.appendChild(img);
    }

    var body = document.createElement('div');
    body.className = 'ra-search__item-body';

    var span = document.createElement('span');
    span.className = 'ra-search__item-title';
    setHighlightedText(span, p.titleHtml || p.title || '', plainTitle, p.query || '');
    body.appendChild(span);

    a.appendChild(body);
    li.appendChild(a);
    return li;
  }

  /**
   * Idle panel (query shorter than MIN_QUERY_LEN): short typing hint.
   * While a fetch is in flight we keep the previous results (no skeleton).
   * @param {HTMLElement} container
   */
  function renderIdleHint(container) {
    container.innerHTML = '';
    var root = document.createElement('div');
    root.className = 'ra-search ra-search--idle';
    root.setAttribute('aria-label', 'Search suggestions');
    var msg = document.createElement('p');
    msg.className = 'ra-search__idle-hint';
    msg.textContent = 'Type at least ' + MIN_QUERY_LEN + ' characters to search auctions';
    root.appendChild(msg);
    container.appendChild(root);
  }

  /**
   * Small Levenshtein distance for typo → correction matching (e.g. diomond → diamond).
   *
   * @param {string} a
   * @param {string} b
   * @returns {number}
   */
  function levenshtein(a, b) {
    var m = a.length;
    var n = b.length;
    if (!m) return n;
    if (!n) return m;
    var prev = new Array(n + 1);
    var curr = new Array(n + 1);
    var i;
    var j;
    for (j = 0; j <= n; j++) prev[j] = j;
    for (i = 1; i <= m; i++) {
      curr[0] = i;
      for (j = 1; j <= n; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      }
      var tmp = prev;
      prev = curr;
      curr = tmp;
    }
    return prev[n];
  }

  /**
   * Seed terms so DYM still works when AMS returns 0 hits for a typo
   * (e.g. "diomond" → no products, still suggest "diamond").
   */
  var RA_DYM_SEED_TERMS = [
    'diamond',
    'gold',
    'silver',
    'platinum',
    'ruby',
    'emerald',
    'sapphire',
    'pearl',
    'opal',
    'ring',
    'rings',
    'necklace',
    'bracelet',
    'earrings',
    'earring',
    'pendant',
    'watch',
    'bangle',
    'charm',
    'hoop',
    'stud',
    'tennis',
    'solitaire',
    'engagement',
    'wedding',
    'jewellery',
    'jewelry',
  ];

  /**
   * Derive a "Did you mean" correction when Prefixbox does not return one
   * (invalid key, missing spellcheck type, or AMS-only fallback). Prefer prefix
   * completions from product/keyword titles (diam → diamond), then close typos.
   *
   * @param {string} query
   * @param {Array<{ title?: string }>} keywords
   * @param {Array<{ title?: string }>} products
   * @returns {string}
   */
  function inferDidYouMean(query, keywords, products) {
    var q = String(query || '')
      .trim()
      .toLowerCase();
    if (q.length < MIN_QUERY_LEN) return '';

    var wordCounts = Object.create(null);

    function consider(text, weight) {
      var words = String(text || '')
        .toLowerCase()
        .match(/[a-z]{3,}/g);
      if (!words) return;
      var wgt = weight || 1;
      for (var i = 0; i < words.length; i++) {
        var w = words[i];
        if (w === q) continue;
        wordCounts[w] = (wordCounts[w] || 0) + wgt;
      }
    }

    var si;
    for (si = 0; si < RA_DYM_SEED_TERMS.length; si++) {
      consider(RA_DYM_SEED_TERMS[si], 2);
    }
    var ki;
    for (ki = 0; ki < (keywords || []).length; ki++) {
      consider(keywords[ki] && (keywords[ki].title || keywords[ki].label), 3);
    }
    var pi;
    for (pi = 0; pi < (products || []).length; pi++) {
      consider(products[pi] && products[pi].title, 5);
    }

    var best = '';
    var bestScore = -1;
    var words = Object.keys(wordCounts);
    for (var wi = 0; wi < words.length; wi++) {
      var w = words[wi];
      var score = -1;
      if (w.indexOf(q) === 0 && w.length > q.length) {
        score = 1000 + wordCounts[w] * 10 + (w.length - q.length);
      } else {
        var dist = levenshtein(q, w);
        if (dist > 0 && dist <= 2 && Math.abs(q.length - w.length) <= 2) {
          score = 500 - dist * 50 + wordCounts[w] * 10;
        }
      }
      if (score > bestScore) {
        bestScore = score;
        best = w;
      }
    }

    return best;
  }

  /**
   * @param {string} correction
   * @param {string} listingUrl
   * @returns {HTMLDivElement | null}
   */
  function buildDidYouMeanSection(correction, listingUrl) {
    var text = String(correction || '').trim();
    if (!text) return null;
    var wrap = document.createElement('div');
    wrap.className = 'ra-search__did-you-mean';
    var a = document.createElement('a');
    a.className = 'ra-search__did-you-mean-link';
    a.href = listingUrl
      ? buildAuctionListingSearchUrl(listingUrl, text)
      : '#';
    // Match TJC.com: Did you mean 'diamond' ? with emphasized term.
    a.appendChild(document.createTextNode("Did you mean '"));
    var term = document.createElement('em');
    term.className = 'ra-search__did-you-mean-term';
    term.textContent = text;
    a.appendChild(term);
    a.appendChild(document.createTextNode("' ?"));
    wrap.appendChild(a);
    return wrap;
  }

  /**
   * @param {{ title?: string, titleHtml?: string, url?: string, query?: string }} k
   */
  function renderKeywordRow(k) {
    var li = document.createElement('li');
    li.className = 'ra-search__item ra-search__item--keyword';
    var a = document.createElement('a');
    a.className = 'ra-search__link ra-search__link--keyword';
    a.href = k.url || '#';
    setHighlightedText(a, k.titleHtml || k.title || '', k.title || '', k.query || '');
    li.appendChild(a);
    return li;
  }

  /**
   * @param {{ title?: string, titleHtml?: string, url?: string, image?: string, query?: string }} c
   */
  function renderCategoryRow(c) {
    var li = document.createElement('li');
    li.className = 'ra-search__item ra-search__item--category';
    var a = document.createElement('a');
    a.className = 'ra-search__link ra-search__link--category';
    a.href = c.url || '#';
    if (c.image) {
      var img = document.createElement('img');
      img.className = 'ra-search__category-thumb';
      img.src = c.image;
      img.alt = '';
      img.loading = 'lazy';
      a.appendChild(img);
    }
    var span = document.createElement('span');
    span.className = 'ra-search__item-title';
    setHighlightedText(span, c.titleHtml || c.title || '', c.title || '', c.query || '');
    a.appendChild(span);
    li.appendChild(a);
    return li;
  }

  /**
   * @param {HTMLElement} container
   * @param {{
   *   products?: unknown[],
   *   categories?: unknown[],
   *   keywords?: unknown[],
   *   didYouMean?: string
   * }} data
   */
  function renderPayload(container, data) {
    container.innerHTML = '';
    var limit = getSuggestionProductLimit();
    var products = ((data && data.products) || []).slice(0, limit);
    var didYouMean = (data && data.didYouMean) || '';
    var listingUrl = getAuctionListingUrl();
    var frag = document.createDocumentFragment();

    var root = document.createElement('div');
    root.className = 'ra-search ra-search--products-only';

    var right = document.createElement('div');
    right.className = 'ra-search__col ra-search__col--right';
    var dymSec = buildDidYouMeanSection(didYouMean, listingUrl);
    if (dymSec) right.appendChild(dymSec);
    var prodSec = buildListSection(
      'Product searches',
      products,
      renderProductRow,
      'ra-search__section--products'
    );
    if (prodSec) right.appendChild(prodSec);

    if (!prodSec && !dymSec) {
      var empty = document.createElement('p');
      empty.className = 'ra-search__empty';
      empty.textContent = 'No results found';
      frag.appendChild(empty);
      container.appendChild(frag);
      return;
    }

    // ≤5 products: one column so an empty second column does not leave white space.
    if (products.length <= 5) {
      root.classList.add('ra-search--single-col');
    }

    root.appendChild(right);
    frag.appendChild(root);
    container.appendChild(frag);
  }

  /**
   * @param {string} baseUrl
   * @param {string} q
   * @param {AbortSignal} signal
   */
  function fetchAuctionSearch(baseUrl, q, signal) {
    var url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('q', q);
    return fetch(url.toString(), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: signal,
    }).then(function (res) {
      if (!res.ok) throw new Error('Auction search failed');
      return res.json();
    });
  }

  /**
   * Clean Prefixbox product URL (drop ?variant= for auction storefront parity).
   *
   * @param {string} href
   * @returns {string}
   */
  function cleanPrefixboxProductUrl(href) {
    var raw = String(href || '').trim();
    if (!raw || raw === '#') return '#';
    try {
      var u = new URL(raw, window.location.origin);
      u.searchParams.delete('variant');
      return u.pathname + (u.search || '') + (u.hash || '');
    } catch (e) {
      return raw;
    }
  }

  /**
   * Maps the raw Prefixbox autocomplete response to the storefront payload shape.
   *
   * @param {{ suggestions?: Array<object> }} raw
   * @param {string} listingUrl
   * @param {string} [query]
   * @returns {{ products: object[], categories: object[], keywords: object[], didYouMean: string }}
   */
  function mapPrefixboxSuggestions(raw, listingUrl, query) {
    var suggestions = (raw && raw.suggestions) || [];
    var products = [];
    var keywords = [];
    var categories = [];
    var didYouMean = '';
    var q = String(query || '').trim();
    var typeLower = '';
    var productLimit = getSuggestionProductLimit();

    for (var i = 0; i < suggestions.length; i++) {
      var s = suggestions[i];
      if (!s || typeof s !== 'object') continue;
      typeLower = String(s.type || '').toLowerCase();

      // Merchandising redirects (e.g. "ring" → /collections/rings) must never surface —
      // auction search stays on the auctions listing with ?q=.
      if (typeLower === 'redirect' || typeLower === 'merchandisingredirect') {
        continue;
      }

      if (
        typeLower === 'didyoumean' ||
        typeLower === 'did_you_mean' ||
        typeLower === 'spellcheck' ||
        typeLower === 'spelling' ||
        typeLower === 'correction' ||
        typeLower === 'correctedquery' ||
        typeLower === 'corrected_query'
      ) {
        var correction = decodeMojibake(
          s.text ||
            s.correctedText ||
            s.correctedQuery ||
            stripHtmlTags(s.displayText || '') ||
            s.query ||
            ''
        );
        if (correction && !didYouMean) didYouMean = correction;
        continue;
      }

      if (typeLower === 'product' || typeLower === 'personalizedprod') {
        if (products.length >= productLimit) continue;
        var productTitle = decodeMojibake(
          s.text || stripHtmlTags(s.displayText || '') || ''
        );
        if (!productTitle) continue;
        products.push({
          title: productTitle,
          titleHtml: s.displayText || productTitle,
          url: cleanPrefixboxProductUrl(s.url || ''),
          image: s.imageUrl || s.image || '',
          query: q,
        });
        continue;
      }

      if (s.type === 'keyword' || s.type === 'personalizedKw') {
        var keywordText = decodeMojibake(s.text || stripHtmlTags(s.displayText || ''));
        keywords.push({
          title: keywordText,
          titleHtml: s.displayText || keywordText,
          url: buildAuctionListingSearchUrl(
            listingUrl || AUCTION_LISTING_FALLBACK_PATH,
            s.text || keywordText
          ),
          query: q,
        });
      } else if (
        s.type === 'category' ||
        s.type === 'personalizedCat' ||
        s.type === 'collection'
      ) {
        var plainCategoryTitle = decodeMojibake(
          s.alternativeDisplayText || s.text || stripHtmlTags(s.displayText || '')
        );
        // Prefer auction listing ?q= over retail collection URLs from Prefixbox.
        categories.push({
          title: plainCategoryTitle,
          titleHtml: s.displayText || plainCategoryTitle,
          url: buildAuctionListingSearchUrl(
            listingUrl || AUCTION_LISTING_FALLBACK_PATH,
            plainCategoryTitle || q
          ),
          image: s.imageUrl || '',
          query: q,
        });
      }
    }

    if (!didYouMean && raw && typeof raw === 'object') {
      didYouMean = decodeMojibake(
        raw.didYouMean ||
          raw.did_you_mean ||
          raw.correctedQuery ||
          raw.corrected_query ||
          raw.spellCheck ||
          raw.spellcheck ||
          ''
      );
    }

    return {
      products: products,
      categories: categories,
      keywords: keywords,
      didYouMean: didYouMean,
    };
  }

  /**
   * Live AMS auctions matching `q`, shaped for the Product searches column.
   * Used only when Prefixbox returns no product suggestions.
   *
   * @param {string} q
   * @param {AbortSignal} signal
   * @returns {Promise<object[]>}
   */
  function fetchLiveAuctionSuggestionProducts(q, signal) {
    var limit = getSuggestionProductLimit();
    var url = new URL('/apps/rising-auctions/active-auctions', window.location.origin);
    url.searchParams.set('q', q);
    url.searchParams.set('limit', String(limit));
    url.searchParams.set('page', '1');
    url.searchParams.set('sort', 'ending-soon');
    return fetch(url.toString(), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal: signal,
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Live auction suggestions failed');
        return res.json();
      })
      .then(function (data) {
        var auctions = ((data && data.auctions) || []).slice(0, limit);
        return auctions.map(function (a) {
          var title = decodeMojibake((a && a.title) || '');
          return {
            title: title,
            titleHtml: title,
            url: (a && a.url) || '#',
            image: (a && a.featuredImageUrl) || '',
            query: q,
          };
        });
      });
  }

  /**
   * @param {{ tracker: string, apiKey: string }} config
   * @param {string} q
   * @param {AbortSignal} signal
   * @param {string} listingUrl
   */
  function fetchPrefixboxAutocomplete(config, q, signal, listingUrl) {
    var url = new URL('https://api.prefixbox.com/autocomplete/suggestions');
    url.searchParams.set('btr', config.tracker);
    url.searchParams.set('pattern', q);
    // Soft-fail Prefixbox (401/invalid key/network) so AMS products still render.
    return fetch(url.toString(), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Ocp-Apim-Subscription-Key': config.apiKey,
      },
      signal: signal,
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Prefixbox autocomplete failed');
        return res.json();
      })
      .then(function (raw) {
        return mapPrefixboxSuggestions(raw, listingUrl, q);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') throw err;
        return {
          products: [],
          categories: [],
          keywords: [],
          didYouMean: '',
        };
      })
      .then(function (mapped) {
        var keywords = mapped.keywords || [];
        var categories = mapped.categories || [];
        var didYouMean = mapped.didYouMean || '';
        var pbProducts = mapped.products || [];

        // Prefer Prefixbox product suggestions for the typed pattern (e.g. "ring").
        // Do not replace them with AMS active-auctions (and never auto-query the
        // Did-you-mean plural like "rings" — that only appears as a DYM link).
        if (pbProducts.length) {
          if (!didYouMean) {
            didYouMean = inferDidYouMean(q, keywords, pbProducts);
          }
          return {
            keywords: keywords,
            categories: categories,
            didYouMean: didYouMean,
            products: pbProducts,
          };
        }

        // Fallback when Prefixbox has no product hits for this pattern.
        return fetchLiveAuctionSuggestionProducts(q, signal)
          .catch(function () {
            return [];
          })
          .then(function (liveProducts) {
            if (!didYouMean) {
              didYouMean = inferDidYouMean(q, keywords, liveProducts);
            }
            return {
              keywords: keywords,
              categories: categories,
              didYouMean: didYouMean,
              products: liveProducts,
            };
          });
      });
  }

  /**
   * Dimmed backdrop behind the suggestions panel (TWS-866).
   * @returns {HTMLElement}
   */
  function getOrCreateOverlay() {
    var el = document.querySelector('.' + OVERLAY_CLASS);
    if (el) return el;
    el = document.createElement('div');
    el.className = OVERLAY_CLASS;
    el.setAttribute('hidden', '');
    el.setAttribute('aria-hidden', 'true');
    document.body.appendChild(el);
    return el;
  }

  /**
   * Gray out only main content; leave the header undimmed for typing.
   * @param {HTMLElement} overlay
   */
  function syncOverlayGeometry(overlay) {
    if (!overlay) return;
    var header = document.querySelector('.global-header');
    var top = 0;
    if (header) {
      top = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    }
    overlay.style.setProperty('--ra-overlay-top', top + 'px');
  }

  /**
   * Anchor suggestions under the header search field.
   * Width follows product density: single-col (≤5) stays compact; two-col uses ~900px.
   *
   * @param {HTMLElement} panelRoot
   * @param {HTMLElement} input
   */
  function syncSuggestionsPanelGeometry(panelRoot, input) {
    if (!panelRoot || !input) return;
    var form = input.closest('.global-header__search') || input;
    var rect = form.getBoundingClientRect();
    var gap = 4;
    var isNarrow = window.innerWidth <= 767;
    var viewportPad = isNarrow ? 12 : 16;
    var maxWidth = Math.min(1200, window.innerWidth - viewportPad * 2);
    var singleCol = !!panelRoot.querySelector('.ra-search--single-col');
    var width;
    var left;
    if (isNarrow) {
      width = maxWidth;
      left = viewportPad;
    } else {
      var target = singleCol ? 560 : 900;
      width = Math.min(maxWidth, Math.max(Math.round(rect.width), Math.min(target, maxWidth)));
      left = Math.round(rect.left);
      if (left + width > window.innerWidth - viewportPad) {
        left = Math.max(viewportPad, window.innerWidth - viewportPad - width);
      }
      if (left < viewportPad) left = viewportPad;
    }
    panelRoot.style.setProperty('--ra-suggestions-top', Math.round(rect.bottom + gap) + 'px');
    panelRoot.style.setProperty('--ra-suggestions-left', left + 'px');
    panelRoot.style.setProperty('--ra-suggestions-width', width + 'px');
  }

  function bindInput(opts) {
    var input = opts.input;
    var resultsContainer = opts.resultsContainer;
    var loadingEl = opts.loadingEl;
    var emptyEl = opts.emptyEl;
    var panelRoot = opts.panelRoot;
    var baseUrl = opts.baseUrl;
    var listingUrl = opts.listingUrl;
    var timeoutId = null;
    var abortController = null;
    var latestSearchId = 0;
    var geometryBound = false;
    var overlay = getOrCreateOverlay();

    function hasRenderedResults() {
      return !!resultsContainer.querySelector('.ra-search:not(.ra-search--idle)');
    }

    function showIdlePanel() {
      if (loadingEl) loadingEl.style.display = 'none';
      if (emptyEl) emptyEl.style.display = 'none';
      renderIdleHint(resultsContainer);
    }

    /** Hide the plain Loading text; never clear results until a successful response. */
    function hideLoadingChrome() {
      if (loadingEl) loadingEl.style.display = 'none';
    }

    /** Invalidate in-flight handlers so a late response cannot wipe current UI. */
    function invalidateInFlightSearches() {
      latestSearchId += 1;
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
    }

    function onGeometryChange() {
      if (panelRoot && panelRoot.classList.contains('active')) {
        syncSuggestionsPanelGeometry(panelRoot, input);
        syncOverlayGeometry(overlay);
      }
    }

    function bindGeometryListeners() {
      if (geometryBound || !panelRoot) return;
      geometryBound = true;
      window.addEventListener('resize', onGeometryChange);
      window.addEventListener('scroll', onGeometryChange, true);
    }

    /**
     * Move the panel onto `body` so sticky auction chrome (CTA bar) cannot
     * paint over it on mobile — fixed-in-header stacking loses to sticky.
     */
    function portalPanelToBody() {
      if (!panelRoot || panelRoot.parentNode === document.body) return;
      panelRoot._raOriginalParent = panelRoot.parentNode;
      panelRoot._raNextSibling = panelRoot.nextSibling;
      document.body.appendChild(panelRoot);
    }

    function restorePanelHome() {
      if (!panelRoot || !panelRoot._raOriginalParent) return;
      var parent = panelRoot._raOriginalParent;
      var next = panelRoot._raNextSibling;
      if (next && next.parentNode === parent) {
        parent.insertBefore(panelRoot, next);
      } else {
        parent.appendChild(panelRoot);
      }
      panelRoot._raOriginalParent = null;
      panelRoot._raNextSibling = null;
    }

    function showPanel() {
      if (!panelRoot) return;
      portalPanelToBody();
      syncSuggestionsPanelGeometry(panelRoot, input);
      syncOverlayGeometry(overlay);
      bindGeometryListeners();
      panelRoot.classList.add('active');
      overlay.removeAttribute('hidden');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add(BODY_OPEN_CLASS);
    }

    function hidePanel() {
      if (panelRoot) panelRoot.classList.remove('active');
      restorePanelHome();
      overlay.setAttribute('hidden', '');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove(BODY_OPEN_CLASS);
    }

    function clearResults() {
      resultsContainer.innerHTML = '';
      if (emptyEl) emptyEl.style.display = 'none';
    }

    /**
     * Apply API data to the panel. Call only for the latest successful search —
     * previous results stay on screen until this runs.
     * @param {unknown} data
     * @param {string} query
     * @param {number} searchId
     */
    function handlePayload(data, query, searchId) {
      if (searchId !== latestSearchId) return;
      if (String(input.value || '').trim() !== String(query || '').trim()) return;

      hideLoadingChrome();
      var products = (data && data.products) || [];
      var categories =
        (data && data.categories) || (data && data.collections) || [];
      var keywords = (data && data.keywords) || [];
      var didYouMean = (data && data.didYouMean) || '';
      var q = String(query || '').trim();
      // AMS / storefront-search have no spellcheck field; Prefixbox often omits it too.
      if (!didYouMean) {
        didYouMean = inferDidYouMean(q, keywords, products);
      }
      if (!products.length && !didYouMean) {
        clearResults();
        if (emptyEl) {
          emptyEl.textContent = 'No results found';
          emptyEl.style.display = 'block';
        }
        return;
      }
      if (emptyEl) emptyEl.style.display = 'none';
      // renderPayload replaces container contents in one paint.
      renderPayload(resultsContainer, {
        products: products,
        didYouMean: didYouMean,
      });
      // Width depends on product count (single-col vs two-col).
      syncSuggestionsPanelGeometry(panelRoot, input);
    }

    function runSearch(q) {
      if (abortController) abortController.abort();
      abortController = new AbortController();
      var signal = abortController.signal;
      var searchId = (latestSearchId += 1);
      // Keep previous results visible until handlePayload runs for this searchId.
      hideLoadingChrome();

      var prefixbox = getPrefixboxConfig();
      var request = prefixbox
        ? fetchPrefixboxAutocomplete(prefixbox, q, signal, listingUrl).catch(function (err) {
            if (err && err.name === 'AbortError') throw err;
            return fetchAuctionSearch(baseUrl, q, signal);
          })
        : fetchAuctionSearch(baseUrl, q, signal);

      request
        .then(function (data) {
          handlePayload(data, q, searchId);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          if (searchId !== latestSearchId) return;
          // Failed request: leave previous results as-is (no UI wipe).
          hideLoadingChrome();
        });
    }

    function openOnFocus() {
      showPanel();
      var q = input.value.trim();
      if (q.length >= MIN_QUERY_LEN) {
        if (timeoutId) clearTimeout(timeoutId);
        // Re-fetch, but keep any existing results until the response arrives.
        timeoutId = setTimeout(function () {
          runSearch(q);
        }, DEBOUNCE_MS);
      } else if (!hasRenderedResults()) {
        // No prior results — show the short typing hint.
        if (timeoutId) clearTimeout(timeoutId);
        invalidateInFlightSearches();
        showIdlePanel();
      }
    }

    input.addEventListener('focus', openOnFocus);
    // Injected on first focus by ra-search-loader.js (TWS-1368), so that focus event has
    // already fired; open now and search anything typed while this script was loading.
    if (document.activeElement === input) openOnFocus();

    input.addEventListener('input', function () {
      var q = input.value.trim();
      if (timeoutId) clearTimeout(timeoutId);
      showPanel();
      if (q.length < MIN_QUERY_LEN) {
        // Stop in-flight work but keep last successful results on screen.
        invalidateInFlightSearches();
        if (!hasRenderedResults()) showIdlePanel();
        return;
      }
      // Do not clear — previous results stay until the next successful response.
      if (abortController) abortController.abort();
      hideLoadingChrome();
      timeoutId = setTimeout(function () {
        runSearch(q);
      }, DEBOUNCE_MS);
    });

    document.addEventListener('click', function (e) {
      if (!panelRoot || !input) return;
      var t = e.target;
      if (!(t instanceof Node)) return;
      if (panelRoot.contains(t) || input.contains(t)) return;
      // Page or dim overlay — close panel + overlay.
      hidePanel();
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') hidePanel();
    });

    var form = input.closest('form');
    if (form) {
      lockAuctionSearchForm(form);
      // Form-level backup; document capture guard (installAuctionSearchSubmitGuard) is primary.
      form.addEventListener(
        'submit',
        function (e) {
          hidePanel();
          var targetListing = listingUrl || getAuctionListingUrl() || AUCTION_LISTING_FALLBACK_PATH;
          e.preventDefault();
          e.stopPropagation();
          if (typeof e.stopImmediatePropagation === 'function') {
            e.stopImmediatePropagation();
          }
          navigateToAuctionListingSearch(targetListing, input.value);
        },
        true
      );
    }
  }

  function init() {
    // Always install Enter/submit + merch Location guards first — even if autocomplete API URL is absent.
    installAuctionSearchSubmitGuard();
    installAuctionMerchandisingNavGuard();
    if (!isAuctionStorefrontScope()) return;

    var headerForm = document.querySelector('form.global-header__search');
    lockAuctionSearchForm(headerForm);

    // Prefixbox autocomplete / preloader suppression runs eagerly in ra-search-loader.js.

    var baseUrl = getAuctionSearchUrl();
    if (!baseUrl) return;

    var listingUrl = getAuctionListingUrl();
    var currentQuery = getCurrentSearchQuery();

    // Header search only — search-modal / predictive search is omitted on auction scope.
    var headerInput = document.querySelector('.global-header__search [data-search-input]');
    var headerPanel = document.querySelector('.global-header__search [data-search-suggestions]');
    if (headerInput && headerPanel) {
      if (currentQuery && !headerInput.value) headerInput.value = currentQuery;
      var headerMount =
        headerPanel.querySelector('[data-ra-search-results]') || headerPanel;
      var headerLoading = headerPanel.querySelector('.global-header__search-suggestions-loading');
      var headerEmpty = headerPanel.querySelector('[data-search-empty]');
      bindInput({
        input: headerInput,
        resultsContainer: headerMount,
        loadingEl: headerLoading,
        emptyEl: headerEmpty,
        panelRoot: headerPanel,
        baseUrl: baseUrl,
        listingUrl: listingUrl,
      });
    }
  }

  // Register document capture + Location merch guards as soon as this deferred script runs.
  // Primary Location patch is in theme.liquid <head>; this refreshes allowlist / backup.
  installAuctionSearchSubmitGuard();
  installAuctionMerchandisingNavGuard();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
